import { BbSelectOption } from './../../bb-select/bb-select.component';
import { Project } from 'app/interfaces/project';
import { MediaclipService } from './../../../../services/mediaclip.service';
import { InteractivityDesignDimensionsService, DesignDimensions, ConversionType } from './interactivity-design-dimensions.service';
import { InteractivityWidgetDefinitionService } from './interactivity-widget-definition.service';
import { Injectable } from '@angular/core';
import { Subject, Subscription, ReplaySubject, Observable, of } from 'rxjs';

import { SapiService, SapiEntity } from '../../../../services/sapi.service';
import { Mediaclip, MediaclipAsset } from '../../../../interfaces/mediaclip';
import { ReduxService } from '../../../../services/redux.service';

import { InteractivityMediaService } from './interactivity-media.service';
import { IDataWidget, IDataTrackAreaItem, ITimelineData, IWidgetGroupTag, IAction, IMessage, ITimelineForm, IOptions, IInOutPoint }
	from '../redux/timeline-data.model';
import { ITimelineDataState } from '../redux/timeline-data.store';
import { TimelineDataActions } from '../redux/timeline-data-actions';
import { Widget } from '../classes/Widget';
import { IWidget } from '../interfaces/IWidget';
import { ITrackAreaItem } from '../interfaces/ITrackAreaItem';
import { StageWidget } from '../classes/StageWidget';
import { TimedWidget } from '../classes/TimedWidget';
import { Track } from '../classes/Track';
import { TrackGroup } from '../classes/TrackGroup';
import { TimelineDataConverter } from '../classes/TimelineDataConverter';
import { AssetService } from '../../../../services/asset.service';
import { WidgetIconPipe } from '../../../../pipes/widgetIcon.pipe';
import { map } from 'rxjs/operators';
import { bbDeepCopy } from '../../../../shared/utils/bbDeepCopy.util';
import { bbGenerateShortId } from '../../../../shared/utils/bbGenerateShortId.util';
import { OverlayService } from '../../../../services/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { WidgetType } from '../enums/WidgetType';

// NB Singleton?!?

@Injectable()
export class InteractivityEditorService {
	public static readonly eventHandlerNames: string[] = ['onclick', 'onhide', 'onenter', 'onshow', 'onmouseover', 'onmouseout',
		'ontransitionDone', 'onclickToggleOn', 'onclickToggleOff', 'onstart', 'onstop', 'ontick', 'oncomplete', 'onfinished', 'onclickToggleOn',
		'onclickToggleOff', 'onvalidationsucceeded', 'onvalidationfailed', 'onSuccess', 'onFailure', 'onAllWidgetsValidated'];

	parentProject: Project;
	projectContextOptions: BbSelectOption[];
	public trackZoomSubject: ReplaySubject<{
		zoomFactor: number;
		offset: number;
	}>;

	public extraTimeMultiplierSubject: ReplaySubject<number>;
	public activeAreaSubject: ReplaySubject<string>;
	public durationSubject: ReplaySubject<number>;
	public groupTagSubject: ReplaySubject<IWidgetGroupTag[]>;
	public publicationLanguagesSubject: ReplaySubject<any[]>;
	public parentEntitySubject: ReplaySubject<SapiEntity>;
	public widgetVisibilitySubject: ReplaySubject<void>;
	public projectContextOptionsChange: ReplaySubject<any>;
	public widgetsAsOptions: any[] = [];
	public localstorageKeys: any[] = [];
	public localstorageValues: any[] = [];
	public variableOptions: any[] = [];
	public messageOptions: any[] = [];
	public operatorList: any[] = [
		{
			label: 'Equals',
			value: '==',
			text: 'is',
			iconText: '='
		},
		{
			label: 'Does not equal',
			value: '!=',
			text: 'is not',
			iconText: '≠'
		},
		{
			label: 'Contains',
			value: 'contains',
			text: 'is',
			iconText: '∋'
		},
		{
			label: 'Does not contain',
			value: 'doesNotContain',
			text: 'does not contain',
			iconText: '∌'
		},
		{
			label: 'Less than',
			value: '<',
			text: 'is less than',
			icon: 'chevron-left'
		},
		{
			label: 'Greater than',
			value: '>',
			text: 'is greater than',
			icon: 'chevron-right'
		}
	];

	public designDimensionsSubject: ReplaySubject<{
		width: number;
		height: number;
		zoomFactor: number;
		fromMedia?: boolean;
	}>;
	public timelineOptionsSubject: ReplaySubject<IOptions>;
	public isTimeBased = true;
	public justConverted: boolean;
	useExitArea: boolean;

	private initialized = false;
	private _activeStateSet = 'default';
	private _activeState = 'default';
	private _tracks: ITrackAreaItem[];
	private _widgets: IWidget[];
	private _widgetsSubject: ReplaySubject<IWidget[]>;
	private _tracksSubject: ReplaySubject<ITrackAreaItem[]>;
	private _messagesSubject: ReplaySubject<IMessage[]>;
	private _formsSubject: ReplaySubject<ITimelineForm[]>;
	private _stateSubscription: Subscription;
	private _parentEntity: any;
	private _parentEntityType: string;
	private _parentEntityId: string;
	private _mediaclipAssetsSubject: ReplaySubject<MediaclipAsset>;
	private subscriptions: Subscription;

	// for optimisation
	private _lastWidgetsJSON: string;
	private _lastOrderJSON: string;
	private _lastMessagesJSON: string;
	private _lastFormsJSON: string;
	private extraTimeMultiplier = 1.1;
	private duration = 0;

	// eslint-disable-next-line id-blacklist
	private clipBoard: any = { any: null };
	private apps: any = {};
	private convertedTimelineCache: any = {};

	private _designDimensions: DesignDimensions;
	private _appWidgetIdsByAppId: any = {};
	private _currentState: ITimelineDataState;
	private entityLoaded = false;
	private haveFormSubmit = false;


	constructor(private sapiService: SapiService,
		private assetService: AssetService,
		private reduxService: ReduxService,
		private widgetIconPipe: WidgetIconPipe,
		private overlayService: OverlayService,
		private translate: TranslateService,
		private interactivityMediaService: InteractivityMediaService,
		private mediaclipService: MediaclipService,
		private widgetDefinitionService: InteractivityWidgetDefinitionService,
		private interactivityDesignDimensionsService: InteractivityDesignDimensionsService
	) {
		this._widgetsSubject = new ReplaySubject<IWidget[]>(1);
		this._tracksSubject = new ReplaySubject<ITrackAreaItem[]>(1);
		this._messagesSubject = new ReplaySubject<IMessage[]>(1);
		this._formsSubject = new ReplaySubject<ITimelineForm[]>(1);
		this.subscriptions = new Subscription();

		this._mediaclipAssetsSubject = new ReplaySubject<MediaclipAsset>(1);
		this.designDimensionsSubject = new ReplaySubject<any>(1);
		this.timelineOptionsSubject = new ReplaySubject<any>(1);
		this.groupTagSubject = new ReplaySubject<IWidgetGroupTag[]>(1);
		this.widgetVisibilitySubject = new ReplaySubject(1);
		this.projectContextOptionsChange = new ReplaySubject(1);
		this.trackZoomSubject = new ReplaySubject(1);
		this.trackZoomSubject.next({
			zoomFactor: 1,
			offset: 0
		});
		this.publicationLanguagesSubject = new ReplaySubject(1);
		this.parentEntitySubject = new ReplaySubject(1);
		this.extraTimeMultiplierSubject = new ReplaySubject(1);
		this.durationSubject = new ReplaySubject(1);
		this.activeAreaSubject = new ReplaySubject(1);

		this.sapiService.getUrl('/language').subscribe((languageData: any) => {
			if (languageData && languageData.items && languageData.items.length) {
				this.publicationLanguagesSubject.next(languageData.items);
			}
		});

		this.interactivityDesignDimensionsService.designDimensionsSubject.subscribe((designDimensions: DesignDimensions) => {
			this._designDimensions = designDimensions;
		});
	}

	static overrideStatesets(sourceWidget: IDataWidget, targetWidget: IDataWidget) {
		// console.log('[InteractivityEditorService] overrideStatesets');
		const targetWidgetIdParts = targetWidget.id.split('.');
		const origWidgetId = targetWidgetIdParts[1];
		sourceWidget.statesets.forEach(sourceStateset => {
			if (sourceStateset && targetWidget.statesets) {
				const targetStateset = targetWidget.statesets.find(stateset => stateset && stateset.name === sourceStateset.name);
				if (targetStateset && sourceStateset.states) {
					sourceStateset.states.forEach(sourceState => {
						if (sourceState && targetStateset.states) {
							const targetState = targetStateset.states.find(state => state && state.name === sourceStateset.name);
							if (targetState && sourceState.config && sourceState.config.widgetoverrides && sourceState.config.widgetoverrides.widgets) {
								const overrideWidget = sourceState.config.widgetoverrides.widgets.find((item: any) => item && item.widgetid === origWidgetId);
								if (overrideWidget && overrideWidget.config) {
									for (const key in overrideWidget.config) {
										if (overrideWidget.config.hasOwnProperty(key)) {
											// eslint-disable-next-line max-len
											// console.log('[InteractivityEditorService] overrideStatesets going to override ' + key + ' with ' + overrideWidget.config[key]);
											targetState.config[key] = overrideWidget.config[key];
										}
									}
								}
							}
						}
					});
				}
			}
		});
	}

	init(timeline: ITimelineData) {
		this.entityLoaded = false;
		this.initialized = true;
		this.subscriptions = new Subscription();
		this.useExitArea = false;
		this._activeStateSet = 'default';
		this._activeState = 'default';
		this._widgets = [];
		this._tracks = [];
		this.apps = {};
		this.convertedTimelineCache = {};
		this._parentEntity = null;
		this.parentProject = null;
		this.projectContextOptions = [];
		this.projectContextOptionsChange.next(this.projectContextOptions);

		this._lastWidgetsJSON = '';
		this._lastOrderJSON = '';
		this._lastFormsJSON = '';

		this._widgetsSubject.next([]);
		this._tracksSubject.next([]);
		this._mediaclipAssetsSubject.next({});
		this.parentEntitySubject.next(this._parentEntity);

		if (timeline) {
			this._parentEntityType = (timeline.parentEntityType) ? timeline.parentEntityType : null;
			this._parentEntityId = (timeline.parentEntityId) ? timeline.parentEntityId : null;
			if (this._parentEntityType && this._parentEntityId) {
				this.loadEntityData(this._parentEntityType, this._parentEntityId);
			}
		}
		this.extraTimeMultiplierSubject.next(this._parentEntityType === 'App' || this._parentEntityType === 'Project' ? 1 : this.extraTimeMultiplier);

		this.subscriptions.add(
			this.interactivityMediaService.durationSubject.subscribe((duration: number) => {
				if (this.entityLoaded) { // prevent getting duration from previously loaded entity
					this.duration = duration;
					// use this line to only have the extra time when using exitArea
					// this.durationSubject.next(this.useExitArea ? (this.extraTimeMultiplier * this.duration) : this.duration);
					this.durationSubject.next((this._parentEntityType === 'App' || this._parentEntityType === 'Project'
						? 1
						: this.extraTimeMultiplier) * this.duration);

					this.checkAndFixWidgetTimes(bbDeepCopy(this._currentState.data));
					this.checkAndFixOutOfBoundWidgets(bbDeepCopy(this._currentState.data), true);
				}

			})
		);
	}

	setActiveArea(area) {
		this.activeAreaSubject.next(area);
	}

	public haveFormSubmitAction(): boolean {
		return this.haveFormSubmit;
	}

	public haveFormWidgets(): boolean {
		return this._widgets.some(widget => widget.type === WidgetType.FormSelect || widget.type === WidgetType.FormTextArea || widget.type === WidgetType.FormTextField);
	}

	public activateReduxSubscription() {
		if (this.initialized) {
			return;
		}
		this._stateSubscription = this.reduxService.stateObs.subscribe((state: ITimelineDataState) => {
			// console.warn('state', state.type, bbDeepCopy( state));
			this._currentState = state;

			if (state && state.data && state.data.modelVersion !== '2' || (state.data as any).timeevents) {
				setTimeout(() => {
					this.justConverted = true; // set to false in entity load
				});
				// convert timeline data
				const tlData: ITimelineData = TimelineDataConverter.convertOldTimelineData(state.data);
				console.warn('Converting from old timeline version to version ', tlData.modelVersion);
				this.overlayService.createToast(this.translate.instant('CONVERTED_OLD_FORMAT_INTERACTIVE_LAYER'), 'check');

				if (tlData.modelVersion === '2') {
					this.reduxService.dispatch({
						type: TimelineDataActions.LOADED,
						data: tlData,
						history: 'reset',
						description: 'Timeline Loaded and converted'
					});
				}
			} else {
				if (state.type === TimelineDataActions.LOADED &&
					(!state.data.options || !state.data.options.designHeight || !state.data.options.designWidth) && this._parentEntity) {
					this.parseMediaInfo();
				}

				if (!this.initialized) {
					this.init(state.data);
				}
				// console.log('Already converted; going to distribute');
				if (state.data && state.data.messages) {
					const newMessagesJSON = JSON.stringify(state.data.messages);
					if (this._lastMessagesJSON !== newMessagesJSON) {

						const messages: IMessage[] = JSON.parse(newMessagesJSON); // make pure deep new object the lazy way

						this.interactivityDesignDimensionsService.convertMessages(messages, ConversionType.PercentageToPixels);
						this._lastMessagesJSON = newMessagesJSON;
						this._messagesSubject.next(messages);
					}
				} else {
					this._lastMessagesJSON = '[]';
					this._messagesSubject.next([]);
				}

				if (state.data && state.data.options) {
					this.timelineOptionsSubject.next(state.data.options);
					this.useExitArea = state.data.options.exitArea;
				}
				// use this line to only have the extra time when using exitArea
				// this.extraTimeMultiplierSubject.next( this.useExitArea === true ? this.extraTimeMultiplier : 1);
				// use this line to only have the extra time when using exitArea
				// this.durationSubject.next(this.useExitArea ? (this.extraTimeMultiplier * this.duration) : this.duration);
				this.durationSubject.next((this._parentEntityType === 'App' || this._parentEntityType === 'Project'
					? 1
					: this.extraTimeMultiplier) * this.duration);


				if (state.data && state.data.forms) {
					const newFormsJSON = JSON.stringify(state.data.forms);
					if (this._lastFormsJSON !== newFormsJSON) {
						this._lastFormsJSON = newFormsJSON;
						this._formsSubject.next(bbDeepCopy(state.data.forms));
					}
				} else if (!state.data.forms) {

					// we do not have a form yet (probably TL saved in new editor without form object), so adding this now
					this._formsSubject.next([{
						name: '',
						method: 'POST',
						action: '',
						email: '',
						formEmailTemplate: '',
						formEmailFrom: '',
						onSuccess: [],
						onFailure: [],
						onAllWidgetsValidated: [],
						id: '1'
					}]);
				}

				if (state.data && state.data.groupTags) {
					this.groupTagSubject.next(state.data.groupTags);
				}

				if (state.data && state.data.widgets && state.data.order) {
					const order: IDataTrackAreaItem[] = [...state.data.order];
					const widgets: IDataWidget[] = [...state.data.widgets];

					this.fetchApps(order, widgets).subscribe((appsObj) => {
						// insert exposed-property widgets and tracks
						this.handleApps(appsObj);
						for (const appId in appsObj) {
							if (appsObj.hasOwnProperty(appId)) {
								const appWrapper = appsObj[appId];
								const appWidgetIds: string[] = this._appWidgetIdsByAppId[appId] || [];
								for (let iaw = 0, law = appWidgetIds.length; iaw < law; iaw++) {
									const appWidgetId = appWidgetIds[iaw] || '#' + iaw;
									const appWidgetTrackIndex = order.findIndex(track => track && track.widgetid === appWidgetId);
									if (appWidgetTrackIndex >= 0) {
										const appWidgetTrackId = order[appWidgetTrackIndex].id || '#' + iaw;

										if (appWrapper && appWrapper.order) {
											const newDataTracks: IDataTrackAreaItem[] = [];
											for (let iwt = 0, lwt = appWrapper.order.length; iwt < lwt; iwt++) {
												const wt = appWrapper.order[iwt]; // short-hand
												if (wt && wt.type && wt.type.match(/^Track$/i)) {
													this._insertDataTrackCopy(wt, appWidgetId, appWidgetTrackId, newDataTracks, order);
												} else if (wt && wt.type && wt.type.match(/^TrackGroup$/i) && wt.tracks) {
													for (let iwtt = 0, latt = wt.tracks.length; iwtt < latt; iwtt++) {
														const wtt = wt.tracks[iwtt]; // short-hand
														if (wtt && wtt.type && wtt.type.match(/^Track$/i)) {
															wtt.fromAppId = appId; // NB not done in handleApps!
															this._insertDataTrackCopy(wtt, appWidgetId, appWidgetTrackId, newDataTracks, order);
														}
													}
												}
											}
											order.splice(appWidgetTrackIndex, 0, ...newDataTracks);
										} else {
											// probably a reference to a template that was deleted
											// console.warn('There is a reference to a template that was deleted.', appId, appWrapper);
										}
									}

									const appWidgetIndex = widgets.findIndex(widget => widget && widget.id === appWidgetId);
									if (appWidgetIndex >= 0) {
										if (appWrapper && appWrapper.widgets) {
											const appWidget = widgets[appWidgetIndex];
											const newDataWidgets: IDataWidget[] = [];
											for (let iww = 0, lww = appWrapper.widgets.length; iww < lww; iww++) {
												const ww = appWrapper.widgets[iww]; // short-hand
												this._insertDataWidgetCopy(ww, appWidgetId, appWidget, newDataWidgets, widgets);
											}
											widgets.splice(appWidgetIndex, 0, ...newDataWidgets);
										}
									}
								}
							}
						}

						// !!! FIRST HANDLE ORDER, THEN THE WIDGETS,
						//     widgets will be ordered using the order array after this !!!
						const orderJSON = JSON.stringify(order);
						if (orderJSON !== this._lastOrderJSON) {
							this.manageTracks(order, widgets); // sets this._tracks
							this._lastOrderJSON = orderJSON;
							this._tracksSubject.next(this._tracks);
						}

						const widgetsJSON = JSON.stringify(widgets);
						if (widgetsJSON !== this._lastWidgetsJSON) {
							this.manageWidgets(widgets); // sets this._widgets
							this._lastWidgetsJSON = widgetsJSON;
							this._widgetsSubject.next(this._widgets);
						}
					});
				}
			}
		});
	}

	// should be called when routed away from the editor (singleton services have no lifecycle hooks)  //TODO
	public cleanup(): void {
		this.initialized = false;
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
		this._stateSubscription.unsubscribe();
	}

	checkAndFixWidgetTimes(tlData: ITimelineData): number {
		// fix widget outpoints if it is higher then the widget inpoint
		let timeAdjustments = 0;
		tlData.order.forEach((t: IDataTrackAreaItem) => {
			if (t.inoutpoints && t.inoutpoints.length) {
				t.inoutpoints.forEach((ioPoint) => {
					if (parseFloat(ioPoint.inpoint) > parseFloat(ioPoint.outpoint)) {
						ioPoint.outpoint = String(parseFloat(ioPoint.inpoint) + 0.08);
						timeAdjustments++;
					}
				});
			}
		});

		if (timeAdjustments) {
			this.overlayService.createToast(this.translate.instant('INCORRECT_TRACKS_CHANGED', { x: timeAdjustments }), 'check');

			this.reduxService.dispatch({
				type: TimelineDataActions.UPDATE_DATA,
				data: tlData,
				description: `Corrected invalid widget times (lower outpoint then inpoint)`
			});
		}

		return timeAdjustments;
	}

	checkAndFixOutOfBoundWidgets(tlData: ITimelineData, updateRedux = false): number {
		// fix widget in-out points if they exceed the current duration
		let timeAdjustments = 0;
		const maxDuration = (this.extraTimeMultiplier * this.interactivityMediaService.duration) + 0.25;
		tlData.order.forEach((t: IDataTrackAreaItem) => {
			if (t.inoutpoints && t.inoutpoints.length) {
				t.inoutpoints.forEach((ioPoint) => {
					let a = 0;
					if (parseFloat(ioPoint.inpoint) > maxDuration) {
						a++;
						ioPoint.inpoint = String(Math.max(0, maxDuration - 2));
					}
					if (parseFloat(ioPoint.outpoint) > maxDuration) {
						a++;
						ioPoint.outpoint = String(maxDuration);
					}
					if (a) {
						timeAdjustments++;
					}
					// also remove % from the IO points
					if (typeof ioPoint.inpoint === 'string') {
						ioPoint.inpoint = ioPoint.inpoint.replace('%', '');
					}
					if (typeof ioPoint.outpoint === 'string') {
						ioPoint.outpoint = ioPoint.outpoint.replace('%', '');
					}
				});
			}
		});

		if (updateRedux && timeAdjustments) {
			this.overlayService.createToast(this.translate.instant('OUT_OF_BOUNDS_TRACKS_CHANGED', { x: timeAdjustments }), 'check');

			this.reduxService.dispatch({
				type: TimelineDataActions.UPDATE_DATA,
				data: tlData,
				description: `Corrected widget times (out of bounds)`
			});
		}

		return timeAdjustments;
	}

	importTimeline(id: string, fromMediaClip?: Mediaclip) {
		// TODO / WIP
		const extraOptions: any = {
			// for now we always use the working state for importing
			state: 'working'
		};
		this.sapiService.get('timeline', id, '', extraOptions).subscribe((timeline: any) => {
			// console.warn('timeline', timeline)
			const tlData: ITimelineData = TimelineDataConverter.convertOldTimelineData(timeline);

			if (fromMediaClip && fromMediaClip.assets && fromMediaClip.assets.length) {
				const asset = fromMediaClip.assets.find((mc) => mc.mediatype === 'MP4_HD')
					|| fromMediaClip.assets.find((mc) => mc.mediatype === 'MP4_MAIN')
					|| fromMediaClip.assets[0];
				if (asset.width && asset.height) {
					// console.warn('asset', asset, 'aw', asset.width, 'ah', asset.height, 'DW', this.designWidth, 'DH', this.designHeight);
					const mcAspectRatio = Number(asset.width) / Number(asset.height);
					const currentAspectRatio = this._designDimensions.width / this._designDimensions.height;
					// console.warn('asset', asset, 'mcAspectRatio', mcAspectRatio, 'aspectRatio', currentAspectRatio);
					if (mcAspectRatio !== currentAspectRatio) {
						// fix aspect ratio's
						// console.warn('fromMediaClip', fromMediaClip)
						tlData.widgets.forEach((widget) => {
							widget.statesets.forEach((stateSet) => {
								stateSet.states.forEach((state) => {
									if (state.config.width && state.config.height) {
										// let w = parseFloat(state.config.width);
										const h = parseFloat(state.config.height);
										state.config.height = h * (currentAspectRatio / mcAspectRatio) + '%';
										const y = parseFloat(state.config.ypos);
										state.config.ypos = y * (currentAspectRatio / mcAspectRatio) + '%';
									}
								});
							});
						});
					}
				}
			}

			const numUpdatedWidgets = this.checkAndFixOutOfBoundWidgets(tlData);

			if (numUpdatedWidgets) {
				this.overlayService.createToast(this.translate.instant('INTERACTIVE_LAYER_IMPORTED_TRACKS_CHANGED', { x: numUpdatedWidgets }), 'check');
			}

			let tlDataString = JSON.stringify(tlData);

			// find all widget ID's and replace them with new unique ID's
			tlData.widgets.forEach((w: IDataWidget) => {
				const regX = new RegExp(`"${w.id}"`, 'g');
				tlDataString = tlDataString.replace(regX, `"${bbGenerateShortId() + '_from_' + w.id}"`);
			});

			tlData.order.forEach((t: IDataTrackAreaItem) => {
				const regX = new RegExp(`"${t.id}"`, 'g');
				tlDataString = tlDataString.replace(regX, `"${String(Math.floor(Math.random() * 9898946546) + 1) + '_from_' + t.id}"`);
			});

			const newTlData = JSON.parse(tlDataString);

			this.reduxService.dispatch({
				type: TimelineDataActions.IMPORT_TIMELINE_DATA,
				tlData: newTlData,
				description: `Imported interactive layer ${id}`
			});
		});
	}

	handleApps(appsObj: any): any {
		for (const appId in appsObj) {
			if (appsObj[appId]) {
				const appWrapper = appsObj[appId];
				const app = appWrapper.app;
				if (app && app.timelines && app.timelines.length) {
					app.convertedTimelines = app.timelines.map((timeline: any) => {
						let convertedTimeline: any;
						if (this.convertedTimelineCache[timeline.id]) {
							convertedTimeline = this.convertedTimelineCache[timeline.id];
						} else {
							convertedTimeline = this.convertedTimelineCache[timeline.id] = TimelineDataConverter.convertOldTimelineData(timeline);
						}
						return convertedTimeline;
					});
					const ct = app.convertedTimelines[0];
					appsObj[appId].order = !ct.order ? [] : ct.order.map((orderItem: any) =>
						({ ...orderItem, ...{ fromAppId: appId } }) // fromAppTrackId not valid yet
					);
					appsObj[appId].widgets = !ct.widgets ? [] : ct.widgets.map((widget: any) => ({ ...widget, ...{ fromAppId: appId } }));
				}
			}
		}
		return appsObj;
	}

	fetchApps(order: IDataTrackAreaItem[], widgets: IDataWidget[]): Observable<any> {
		const appIdIndex: any = {};
		const appIds: string[] = [];
		let track: IDataTrackAreaItem;

		this._appWidgetIdsByAppId = {};
		if (widgets && widgets.length) {
			for (let i = 0; i < widgets.length; i++) {
				const widget = widgets[i];
				if (widget && widget.statesets && widget.statesets.length) {
					widget.statesets.forEach((stateSet: any) => {
						if (stateSet && stateSet.states && stateSet.states.length) {
							stateSet.states.forEach((state: any) => {
								if (state && state.config && state.config.appid) { // app widget
									// bookkeeping
									if (!this._appWidgetIdsByAppId[state.config.appid]) {
										this._appWidgetIdsByAppId[state.config.appid] = [];
									}
									if (this._appWidgetIdsByAppId[state.config.appid].indexOf(widget.id) < 0) {
										this._appWidgetIdsByAppId[state.config.appid].push(widget.id);
									}

									// cache apps
									if (!this.apps[state.config.appid]) {
										track = order.find(trackItem => trackItem && trackItem.widgetid === widget.id);
										if (track) {
											let appIndex = -1;
											if (widget.apps && (appIndex = widget.apps.findIndex((app: any) => app && app.id === state.config.appid)) > -1) {
												// console.warn('found app in datastructure');
												this.apps[state.config.appid] = {
													index: i,
													app: widget.apps[appIndex],
													trackId: track.id
												};
											} else {
												// console.warn('app not found, adding ID to appIdIndex');
												appIdIndex[state.config.appid] = i;
												appIds.push(state.config.appid);
											}
										}
									}
								}
							});
						}
					});
				}
			}
		}

		if (appIds.length) { // apps to retrieve
			return this.sapiService.getMulti('app', appIds).pipe(
				map((appData: any) => {
					if (appData && appData.length) {
						appData.forEach((app) => {
							if (app && app.id) {
								this.apps[app.id] = {
									index: appIdIndex[app.id], // OBSOLETE?
									app,
									trackId: track.id // OBSOLETE?
								};
							}
						});
					}
					return this.apps;
				})
			);
		} else { // nothing to retrieve
			return of(this.apps);
		}
	}

	parseProjectInfo(project: any) {
		this.projectContextOptions = project?.contexts?.map(c => ({
			label: c.label,
			value: c.code
		})) || [];
		this.projectContextOptionsChange.next(this.projectContextOptions);
		this.interactivityMediaService.duration = 100;
		this.interactivityMediaService.currentTime = 0;
		const savedWidth = this._currentState && this._currentState.data && this._currentState.data.options && this._currentState.data.options.designWidth;
		const savedHeight = this._currentState && this._currentState.data && this._currentState.data.options && this._currentState.data.options.designHeight;
		this.interactivityDesignDimensionsService.setDesignDimensions({
			width: savedWidth || project.width || 1920,
			height: savedHeight || project.height || 1080,
			zoomFactor: this._designDimensions.zoomFactor,
			fromMedia: true
		}, true, !(!savedWidth || !savedHeight));
	}

	parseAppInfo(app: any) {
		this.interactivityMediaService.duration = 100;
		this.interactivityMediaService.currentTime = 0;
		this.interactivityDesignDimensionsService.setDesignDimensions({
			width: app.width || 1920,
			height: app.height || 1080,
			zoomFactor: this._designDimensions.zoomFactor,
			fromMedia: true
		}, true, !(!app.width || !app.width));
	}

	parseMediaInfo() {
		let currentAssetSize = 0;
		let selectedAssetSize = 0;

		let mediaclipAsset: MediaclipAsset;

		if (this._parentEntity && this._parentEntity.assets && this._parentEntity.assets.length > 0) {
			this._parentEntity.assets.forEach((asset: MediaclipAsset) => {
				switch (asset.mediatype) {
					case 'MP4_HD':
					case 'MP4_MAIN':
						if (asset.height > asset.width) {
							currentAssetSize = parseInt(asset.height, 10);
						} else {
							currentAssetSize = parseInt(asset.width, 10);
						}

						if (!selectedAssetSize || (currentAssetSize > selectedAssetSize && currentAssetSize <= 1280)) {
							asset.src = this.assetService.getAssetUrl(this._parentEntity, asset.src);
							mediaclipAsset = asset;
							selectedAssetSize = currentAssetSize;
						}
						break;
					case 'AAC':
					case 'MP3':
						// prefer AAC
						if (asset.mediatype === 'AAC') {
							mediaclipAsset = asset;
							mediaclipAsset.src = this.assetService.getAssetUrl(this._parentEntity);
						} else if (!mediaclipAsset) {
							mediaclipAsset = asset;
							mediaclipAsset.src = this.assetService.getAssetUrl(this._parentEntity);
						}

						break;
				}
			});
			if (mediaclipAsset) {
				if (!mediaclipAsset.exactlength && mediaclipAsset.length) {
					mediaclipAsset.exactlength = String(parseFloat(mediaclipAsset.length) * 1000);
				}
				this.interactivityMediaService.duration = parseInt(mediaclipAsset.exactlength, 10) / 1000;

				const savedWidth = this._currentState && this._currentState.data && this._currentState.data.options
					&& this._currentState.data.options.designWidth;

				const savedHeight = this._currentState && this._currentState.data && this._currentState.data.options
					&& this._currentState.data.options.designHeight;

				this.interactivityDesignDimensionsService.setDesignDimensions({
					width: savedWidth || this._parentEntity.width || Number(mediaclipAsset.width) || 1920,
					height: savedHeight || this._parentEntity.height || Number(mediaclipAsset.height) || 1080,
					zoomFactor: this._designDimensions.zoomFactor,
					fromMedia: true
				}, true, !(!savedWidth || !savedHeight));

				this._mediaclipAssetsSubject.next(mediaclipAsset);
			}
		}
	}


	// *************************************************************************
	// ********************* getters / setters  start **************************
	get parentEntityType() {
		return this._parentEntityType;
	}

	get parentEntity() {
		return this._parentEntity;
	}

	get widgetsSubject(): Subject<IWidget[]> {
		return this._widgetsSubject;
	}

	get messagesSubject(): Subject<IMessage[]> {
		return this._messagesSubject;
	}

	get formsSubject(): Subject<ITimelineForm[]> {
		return this._formsSubject;
	}

	get tracksSubject(): Subject<ITrackAreaItem[]> {
		return this._tracksSubject;
	}

	get widgets(): IWidget[] {
		return this._widgets;
	}

	get activeStateSet(): string {
		return this._activeStateSet;
	}
	set activeStateSet(stateSet: string) {
		this._activeStateSet = stateSet;
		// update widgets
		for (let i = 0, l: number = this._widgets.length; i < l; i++) {
			this._widgets[i].changeStateSet(stateSet);
		}
	}
	get activeState(): string {
		return this._activeState;
	}
	set activeState(state: string) {
		this._activeState = state;
		// update widgets
		for (let i = 0, l: number = this._widgets.length; i < l; i++) {
			this._widgets[i].changeState(state);
		}
	}

	get mediaclipAssetSubject(): Subject<MediaclipAsset> {
		return this._mediaclipAssetsSubject;
	}

	public getUsedStates(config) {
		const usedStates = {};
		// loop through all statesets and states and add them to the usedStates object
		for (let i = 0; i < config?.statesets?.length; i++) {
			const stateset = config.statesets[i];
			for (let j = 0; j < stateset.states.length; j++) {
				const state = stateset.states[j];
				if (Object.keys(state?.config).length) {
					usedStates[state.name] = true;
				}
			}
		}
		return usedStates;
	}

	public getTrackById(id: string): ITrackAreaItem {
		return this._tracks.find((track: ITrackAreaItem) => track && track.id === id);
	}

	public getTrackByWidgetId(id: string): ITrackAreaItem {
		return this._tracks.find((track: ITrackAreaItem) => track && track.widgetId === id);
	}

	public getWidgetById(id: string): IWidget {
		for (let i = 0, l: number = this._widgets.length; i < l; i++) {
			const widget: IWidget = this._widgets[i];
			if (widget && widget.id === id) {
				return widget;
			}
		}
		return null;
	}

	public getWidgetTypeForId(widgetId: string): any {
		const option = this.widgetsAsOptions.find(opt => (opt && opt.value === widgetId));
		return (option) ? option.type : null;
	}

	public getSelectedTracks(selectedWidgets: IWidget[], collapsedGroups: any): any[] {
		const selectedWidgetIds = selectedWidgets.map(selectedWidget => selectedWidget.id);
		const selectedTracks: any[] = [];
		const tracks = [...this._tracks];

		// Flatten selected tracks
		for (let i = 0, l: number = tracks.length; i < l; i++) {
			if (tracks[i] instanceof Track) {
				if (selectedWidgetIds.indexOf(tracks[i].widgetId) !== -1) {
					selectedTracks.push({
						id: tracks[i].id,
						type: 'track',
						child: tracks[i]
					});
				}
			} else if (tracks[i] instanceof TrackGroup && (tracks[i] as TrackGroup).tracks) {
				const groupId = tracks[i].id;
				for (let j = 0, k: number = (tracks[i] as TrackGroup).tracks.length; j < k; j++) {
					const trackInGroup = ((tracks[i] as TrackGroup).tracks[j] as Track);
					if (selectedWidgetIds.indexOf(trackInGroup.widgetId) !== -1) {
						if (j === 0 && (tracks[i] as TrackGroup).tracks.every(item => selectedWidgetIds.indexOf(item.widgetId) !== -1)) {
							selectedTracks.push({
								id: groupId,
								type: 'trackGroup',
								child: tracks[i]
							});
						}
						if (!collapsedGroups[groupId]) {
							selectedTracks.push({
								id: trackInGroup.id,
								groupId,
								type: 'trackInGroup',
								child: trackInGroup
							});
						}
						if (j === k - 1 && (tracks[i] as TrackGroup).tracks.every(item => selectedWidgetIds.indexOf(item.widgetId) !== -1)) {
							selectedTracks.push({
								id: 'end_' + tracks[i].id,
								type: 'trackGroupEnd'
							});
						}
					}
				}
			}
		}
		if (selectedTracks && selectedTracks.length > 0) {
			return selectedTracks;
		} else {
			return [];
		}
	}
	// ********************* getters / setters  end ****************************
	// *************************************************************************


	public createWidgetFromData(widgetData: IDataWidget): IWidget {
		let newWidget: IWidget;
		switch (widgetData.widgetdefinitiontype) {
			case 'rectangle':
			case 'image':
			case 'video':
			case 'button':
			case 'textLabel':
			case 'app':
			case 'formTextField':
			case 'formSelect':
			case 'formTextArea':
			case 'custom':
				newWidget = new StageWidget(widgetData);
				break;
			case 'audio':
			case 'chapter':
				newWidget = new TimedWidget(widgetData);
				break;
			case 'timer':
			default:
				newWidget = new Widget(widgetData);
				break;
		}
		// convert appropriate values in widget to pixels`
		newWidget = this.interactivityDesignDimensionsService.convertWidget(newWidget, ConversionType.PercentageToPixels);
		return newWidget;
	}

	public getExposedDefaultsForWidget(widget: IWidget): any {
		const exposedDefaults: any = {};

		if (widget) {
			if (widget.id &&
				widget.fromAppId &&
				this.apps &&
				this.apps[widget.fromAppId]) {
				const widgetIdParts = widget.id.split('.');
				const origWidgetId = widgetIdParts[1];
				let exposedProperties: string[] = [];
				if (this.apps[widget.fromAppId].app &&
					this.apps[widget.fromAppId].app.exposedProperties &&
					this.apps[widget.fromAppId].app.exposedProperties[origWidgetId]) {
					exposedProperties = this.apps[widget.fromAppId].app.exposedProperties[origWidgetId];
				}
				if (this.apps[widget.fromAppId].widgets) {
					const myDataWidgets: IDataWidget[] = this.apps[widget.fromAppId].widgets;
					const myDataWidget: IDataWidget = myDataWidgets.find((item: IDataWidget) => item && item.id === origWidgetId);
					const myWidget = new Widget(myDataWidget); // NB stateSet, state?!?
					exposedProperties.map((key: string) => exposedDefaults[key] = (myWidget.config[key] !== undefined) ? myWidget.config[key] : '');
				}
			} else if (widget.type) { // normal widget
				// exposedDefaults = this.widgetDefinitionService.getWidgetDefaultsForType(widget.type);
			}
		}

		return exposedDefaults;
	}

	/**
	 * Get ALL widgets that belong to the app widget that this widget belongs to
	 */
	public getOtherAppWidgets(widget: IWidget): IWidget[] {
		let ret: IWidget[] = [];

		if (widget && widget.fromAppId) { // might check for '.' in widget id instead
			const widgetIdParts = widget.id.split('.');
			const appWidgetId = widgetIdParts[0];
			ret = this.widgets &&
				this.widgets.filter(w => (w && w.id && (w.id === appWidgetId || w.id.indexOf(appWidgetId + '.') === 0)));
		}

		return ret;
	}


	public updateInOutPoint(trackId: string, ioPoint: IInOutPoint, inPoint: string, outPoint: string): void {
		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_IN_OUT_POINT,
			trackId,
			inOutPointId: ioPoint.id,
			inPoint,
			outPoint,
			description: 'Changing time for track: ' + trackId
		});
	}

	public addMessage(messageName: string) {
		this.reduxService.dispatch({
			type: TimelineDataActions.ADD_MESSAGE,
			messageName,
			description: 'Added message handler: ' + messageName
		});
	}

	public renameMessage(messageName: string, newMessageName: string, msg: string = null) {
		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_MESSAGE,
			messageName,
			newMessageName,
			description: msg
		});
	}

	public changeMessage(key: string, val: any, msg: string = null) {
		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_MESSAGE,
			messageName: key,
			messageProcedures: val,
			description: msg
		});
	}

	public removeMessage(key: string, msg: string = null) {
		this.reduxService.dispatch({
			type: TimelineDataActions.REMOVE_MESSAGE,
			messageName: key,
			description: msg
		});
	}

	public removeWidgets(widgetIds: string[], msg: string = null) {
		this.reduxService.dispatch({
			type: TimelineDataActions.REMOVE_WIDGETS,
			ids: widgetIds,
			description: msg
		});
	}

	public removeWidget(widgetId: string, msg: string = null) {
		this.reduxService.dispatch({
			type: TimelineDataActions.REMOVE_WIDGET,
			id: widgetId,
			description: msg
		});
	}

	public removeTrackGroup(groupId: string, msg: string = null) {
		this.reduxService.dispatch({
			type: TimelineDataActions.REMOVE_TRACKGROUP,
			id: groupId,
			description: msg
		});
	}

	public ungroupTrackGroup(groupId: string, msg: string = null) {
		this.reduxService.dispatch({
			type: TimelineDataActions.UNGROUP_TRACKGROUP,
			id: groupId,
			description: msg
		});
	}

	public setTmpState(state: any) {
		const previousState = this.activeState;
		this.activeState = state.name;
		setTimeout(() => {
			this.activeState = previousState;
		}, 10);
	}

	public changeWidgetsProperty(widgets: IWidget[], key: string, val: any, msg: string = null): void {

		// change val to percentages (only handlers for now, widgets are done in widget service, needs refactor)
		if (this.interactivityDesignDimensionsService.eventHandlerNames.indexOf(key) > -1) {
			this.interactivityDesignDimensionsService.convertEventHandler(val, ConversionType.PixelsToPercentage);
		}
		if (widgets.length === 0) {
			return;
		}

		let description: string = 'Change ' + key + ' of widgets:';
		if (msg !== null) {
			description = msg;
		}

		if (key.indexOf('/') > -1) { // complex key
			const widgets_: IWidget[] = [];
			for (let i = 0, l = widgets.length; i < l; i++) {
				const widget: IWidget = widgets[i];
				if (widget) {
					if (widget.fromAppId && widget.id) {
						const widgetIdParts = widget.id.split('.');
						const appWidgetId = widgetIdParts[0]; // NB!
						const origWidgetId = widgetIdParts[1]; // NB!
						const appWidget = this.widgets.find((item: any) => item && item.id === appWidgetId);
						let widget_ = widgets_.find((item: any) => item && item.id === appWidgetId);
						if (!widget_) { // not found
							if (appWidget) { // found
								widget_ = { ...appWidget }; // shallow clone
								if (!widgets_.some((item: any) => item && item.id === widget_.id)) {
									widgets_.push(widget_);
								}
							}
						}
						if (widget_) { // found or created
							if (!widget_.id) {
								widget_.id = appWidgetId;
							} // don't ask...
							if (!widget_.config) {
								widget_.config = {};
							}
							if (!widget_.config.widgetoverrides) {
								widget_.config.widgetoverrides = bbDeepCopy(appWidget.config.widgetoverrides || {});
							}
							if (!widget_.config.widgetoverrides.widgets) {
								widget_.config.widgetoverrides.widgets = bbDeepCopy(appWidget.config.widgetoverrides
									&& appWidget.config.widgetoverrides.widgets || []);
							}

							let override = widget_.config.widgetoverrides.widgets.find((item: any) => item && item.widgetid === origWidgetId);
							if (!override) {
								override = { widgetid: origWidgetId, config: {} };
								widget_.config.widgetoverrides.widgets.push(override);
							}
							this._expandComplexKey(key, val, widgets[i].getConfigByState(this.activeStateSet, this.activeState), override.config); // NB!
						}
					} else {
						const widget_: IWidget = { ...widget }; // shallow clone
						widget_.id = (widget as any)._id; // don't ask...
						widget_.config = {};
						this._expandComplexKey(key, val, widgets[i].getConfigByState(this.activeStateSet, this.activeState), widget_.config);
						widgets_.push(widget_);
					}
				}
			}

			// console.log('[InteractivityEditorService] changeWidgetsProperty widgets_: ', widgets_);
			this.reduxService.dispatch({
				type: TimelineDataActions.CHANGE_WIDGETS_DIFFERENT_CONFIG,
				widgets: widgets_,
				stateSet: this.activeStateSet,
				state: this.activeState,
				description
			});
		} else { // simple key
			const widgetConfig: any = {};
			const widgetIds: string[] = [];
			const widgets_: IWidget[] = [];
			for (let i = 0, l: number = widgets.length; i < l; i++) {
				const widget: IWidget = widgets[i];
				if (widget) {
					if (widget.fromAppId && widget.id) {
						const widgetIdParts = widget.id.split('.');
						const appWidgetId = widgetIdParts[0]; // NB!
						const origWidgetId = widgetIdParts[1]; // NB!
						const appWidget = this.widgets.find((item: any) => item && item.id === appWidgetId);
						let widget_ = widgets_.find((item: any) => item && item.id === appWidgetId);
						if (!widget_) { // not found
							if (appWidget) { // found
								widget_ = { ...appWidget }; // shallow clone
								if (!widgets_.some((item: any) => item && item.id === widget_.id)) {
									widgets_.push(widget_);
								}
							}
						}
						if (widget_) { // found or created
							if (!widget_.id) {
								widget_.id = appWidgetId;
							} // don't ask...
							if (!widget_.config) {
								widget_.config = {};
							}
							if (!widget_.config.widgetoverrides) {
								widget_.config.widgetoverrides = bbDeepCopy(appWidget.config.widgetoverrides || {});
							}
							if (!widget_.config.widgetoverrides.widgets) {
								widget_.config.widgetoverrides.widgets = bbDeepCopy(appWidget.config.widgetoverrides
									&& appWidget.config.widgetoverrides.widgets || []);
							}
							let override = widget_.config.widgetoverrides.widgets.find((item: any) => item && item.widgetid === origWidgetId);
							if (!override) {
								override = { widgetid: origWidgetId, config: {} };
								widget_.config.widgetoverrides.widgets.push(override);
							}
							override.config[key] = val;
							// exception for font
							if (key === 'font') {
								this._setFontInConfig(val, override.config);
							}
						}
					} else { // normal
						widgetConfig[key] = val;
						// exception for font
						if (key === 'font') {
							this._setFontInConfig(val, widgetConfig);
						}
						if (widgetIds.indexOf(widget.id) < 0) {
							widgetIds.push(widget.id);
						}
					}
				}
			}

			// console.log('[InteractivityEditorService] changeWidgetsProperty widgetIds: ', widgetIds);
			// console.log('[InteractivityEditorService] changeWidgetsProperty widgetConfig: ', widgetConfig);
			if (widgetIds && widgetIds.length > 0) {
				this.reduxService.dispatch({
					type: TimelineDataActions.CHANGE_WIDGETS_SAME_CONFIG,
					ids: widgetIds,
					config: widgetConfig,
					stateSet: this.activeStateSet,
					state: this.activeState,
					description
				});
			}

			// console.log('[InteractivityEditorService] changeWidgetsProperty widgets_: ', widgets_);
			if (widgets_ && widgets_.length > 0) {
				this.reduxService.dispatch({
					type: TimelineDataActions.CHANGE_WIDGETS_DIFFERENT_CONFIG,
					widgets: widgets_,
					stateSet: this.activeStateSet,
					state: this.activeState,
					description
				});
			}
		}
	}


	public changeFormField(fieldName: string, value: any, msg: string = null): void {
		const description = msg || `Changed form field ${fieldName} to ${value || ''}`;

		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_FORM_FIELD,
			fieldName,
			value,
			description
		});
	}

	public changeWidgetLabel(widgetId: string, label: any, msg: string = null): void {
		let description = 'Change label of widget:';
		if (msg !== null) {
			description = msg;
		}
		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_WIDGET,
			id: widgetId,
			label,
			description
		});
	}

	public changeWidgetTags(widget: IWidget, tags: any, msg: string = null, newTag?: any): void {
		let description = 'Change tags of widget:';
		if (msg !== null) {
			description = msg;
		}
		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_WIDGET,
			id: widget.id,
			tags,
			newTag,
			description
		});
	}

	public addRemoveTagsOnWidget(widgets: IWidget[], tag: IWidgetGroupTag, newTag?: any, remove = false): void {
		const description: string = (remove ? 'Remove' : 'Add') + ` tag ${tag.label} from ${widgets.length} widgets`;
		this.reduxService.dispatch({
			type: TimelineDataActions.ADD_REMOVE_TAG_ON_WIDGETS,
			widgets,
			tag,
			newTag,
			remove,
			description
		});
	}


	public createTrackGroup(tracks: ITrackAreaItem[]): void {
		const newTrackGroup: IDataTrackAreaItem = {
			id: String(Math.floor(Math.random() * 9898946546) + 1),
			type: 'trackgroup',
			tracks: []
		};

		this.reduxService.dispatch({
			type: TimelineDataActions.ADD_TRACKGROUP,
			trackGroup: newTrackGroup,
			tracks: tracks.reverse(),
			description: 'Create new trackgroup'
		});
	}

	public ungroup(trackGroup: TrackGroup) {
		this.ungroupTrackGroup(trackGroup.id);
	}

	public changeGroupLabel(group: ITrackAreaItem, label: any, msg: string = null): void {
		let description = 'Change label of group:';
		if (msg !== null) {
			description = msg;
		}
		this.reduxService.dispatch({
			type: TimelineDataActions.CHANGE_TRACKGROUP,
			id: group.id,
			label,
			description
		});
	}

	public toggleWidgetVisibilityMulti(widgets: IWidget[], visible) {
		widgets.forEach((widget) => {
			this.toggleWidgetVisibility(widget, visible ? 'on' : 'off');
		});
	}

	public toggleWidgetVisibility(w: IWidget, force?: 'on' | 'off') {
		if (w && w instanceof StageWidget) {
			// first handle selected widget itself
			w.visibility = (w.visibility === 'visible' ? 'hidden' : 'visible');
			if (force) {
				if (force === 'on') {
					w.visibility = 'visible';
				} else {
					w.visibility = 'hidden';
				}
			}

			// now see if we have an appWidget or a widget from an app
			const appId: string = w.getConfigValue('appid');
			if (appId !== null) {
				// we have an app widget, handle visibility for all widget in the app
				for (let i = 0; i < this._widgets.length; i++) {
					const widget: IWidget = this._widgets[i];
					if (widget.fromAppId && widget.fromAppId === appId && widget instanceof StageWidget) {
						if (widget instanceof StageWidget) {
							widget.visibility = w.visibility;
						}
					}
				}
			} else if (w.fromAppId) {
				// widget is From an app, handle visibility Of app if needed  (rules in ticket 4609)
				let nrOfWidgetsInAppVisible = 0;
				let appWidget: IWidget;

				// look for other widgets in the same app on Stage
				for (let i = 0; i < this._widgets.length; i++) {
					const widget: IWidget = this._widgets[i];

					if (widget.fromAppId && widget.fromAppId === w.fromAppId && widget instanceof StageWidget) {
						if (widget.visibility === 'visible') {
							nrOfWidgetsInAppVisible++;  // count widgets in app that are visible on stage
						}
					}

					if (widget.getConfigValue('appid') === w.fromAppId) {  // found app widget itself, use after for-loop
						appWidget = widget;
					}
				}

				if (appWidget && appWidget instanceof StageWidget) {
					if (w.visibility === 'visible') {
						appWidget.visibility = 'visible';
					} else if (nrOfWidgetsInAppVisible === 0) {	// w.visibility === 'hidden' -> check if all widgets in app are hidden
						appWidget.visibility = 'hidden';
					}
				}
			}
		}
		this.widgetVisibilitySubject.next();
	}


	public dispatchNewOrder(tracks: ITrackAreaItem[], chapters: ITrackAreaItem[]) {
		const reversedTracks = [...tracks].map(trackItem => this.mapTracks(trackItem)).reverse();
		reversedTracks.forEach((track: ITrackAreaItem) => {
			if (track instanceof TrackGroup && track.tracks) {
				(track as any).tracks = [...track.tracks].reverse();
			}
		});

		const completeTracks = reversedTracks.concat(chapters); // push chapters back in

		this.reduxService.dispatch({
			type: TimelineDataActions.UPDATE_TRACK_ORDER,
			trackOrder: completeTracks,
			description: 'Changed the order of the tracks'
		});
	}

	public setUseEndscreen(useEndscreen: boolean) {
		this.reduxService.dispatch({
			type: TimelineDataActions.SET_ENDSCREEN_AREA,
			useEndscreen,
			description: useEndscreen ? 'Showing interactivity in the endscreen' : 'Hiding interactivity in the endscreen'
		});
	}

	public clipboardSet(item: any, itemType: string = 'any') {
		this.clipBoard[itemType] = item;
	}

	public clipboardGet(itemType: string = 'any'): any {
		return this.clipBoard[itemType];
	}

	public loadEntityData(entityType: string, entityId: string, fromTemplate = false) {
		if (!fromTemplate) {
			this.entityLoaded = false;
		}
		if (entityType.toLowerCase() === 'mediaclip') {
			entityType = 'commonclip';
		}
		this.sapiService.get(entityType.toLowerCase(), entityId).subscribe((entity: any) => {
			if (!fromTemplate) {
				this._parentEntity = entity;
				this.parentEntitySubject.next(this._parentEntity);
				this.entityLoaded = true;
				if (entity.type === 'MediaClip') {
					this.isTimeBased = true;
					this.getClipProjectRelation(entity);
					this.parseMediaInfo();
				} else if (entity.type === 'App') {
					this.isTimeBased = false;
					this.parseAppInfo(entity);
				} else if (entity.type === 'Project') {
					this.isTimeBased = false;
					this.parseProjectInfo(entity);
				}
			} else if (entity.type === 'Project') {
				// setting project contexts for template (App)
				const project = entity as Project;
				this.projectContextOptions = project?.contexts?.map(c => ({
					label: c.label,
					value: c.code
				})) || [];
				this.projectContextOptionsChange.next(this.projectContextOptions);
			}
		}, (err: any) => {
			console.error('Unable to load entity: ' + entityId);
		});
	}

	private _fillWidgetDefaults(widgetData: IDataWidget): void {
		// add default values to the default state config
		const widgetDefaultState = widgetData?.statesets?.[0].states?.[0];
		if (!widgetDefaultState) {
			return;
		}
		const widgetDefaultValues = this.widgetDefinitionService.getWidgetDefaultsForType(widgetData.widgetdefinitiontype);
		if (widgetDefaultValues?.textFormat?.textFormat && widgetDefaultState?.config?.textFormat?.textFormat) {
			widgetDefaultState.config.textFormat.textFormat = {...widgetDefaultValues.textFormat.textFormat, ...widgetDefaultState.config.textFormat.textFormat};
		}
		widgetDefaultState.config = widgetDefaultValues ? {...widgetDefaultValues, ...widgetDefaultState.config} : widgetDefaultState.config;
	}

	// add, remove or update IWidget data in to Widget Array
	private manageWidgets(iDataWidgets: IDataWidget[]): void {
		// first check if 1 or more widgets have been removed, and remove them from _widgets
		let ind: number = this._widgets.length;
		while (ind--) {
			const iWidget: IDataWidget = iDataWidgets.find(w => w.id === this._widgets[ind].id);
			if (iWidget === undefined) {
				this._widgets.splice(ind, 1);
			}
		}
		//  next, check if widgets are added or changed
		for (let i = 0, l: number = iDataWidgets.length; i < l; i++) {
			if (!iDataWidgets[i]) {
				continue;
			} // sanity

			let widget: IWidget = this._widgets.find(w => w && w.id === iDataWidgets[i].id);

			if (widget === undefined) {
				// we don't have this widget yet, adding it
				this._fillWidgetDefaults(iDataWidgets[i]);
				const newWidget = this.createWidgetFromData(iDataWidgets[i]);

				this._widgets.push(newWidget);
			} else {
				// widget already exists, always update
				widget.update(iDataWidgets[i], this.activeStateSet, this.activeState);

				// convert appropriate values in widget to pixels`
				widget = this.interactivityDesignDimensionsService.convertWidget(widget, ConversionType.PercentageToPixels);

			}
		}

		// sort widgets by order array
		// this._widgets.sort((a: IWidget, b: IWidget) => { return (this._tracks.indexOf(a.id) > this._tracks.indexOf(b.id)); });

		// sort widgets by order array
		const sortedWidgets: IWidget[] = [];
		const tracks = this._tracks;
		this.widgetsAsOptions = [];
		this.localstorageKeys = [];
		this.localstorageValues = [];
		this.variableOptions = [];
		this.messageOptions = [];
		this.haveFormSubmit = false;
		for (let i = 0, l: number = tracks.length; i < l; i++) {
			if (tracks[i] instanceof Track) {
				const foundWidget = this._widgets.find(widget => widget && widget.id === (tracks[i] as Track).widgetId);

				if (foundWidget) {
					this.addSelectorOptions(foundWidget);
					sortedWidgets.push(foundWidget);
				}
			} else if (tracks[i] instanceof TrackGroup && (tracks[i] as TrackGroup).tracks) {
				for (let j = 0, k: number = (tracks[i] as TrackGroup).tracks.length; j < k; j++) {
					const foundWidgetInGroup = this._widgets
						.find(widget => widget && widget.id === ((tracks[i] as TrackGroup).tracks[j] as Track).widgetId);

					if (foundWidgetInGroup) {
						this.addSelectorOptions(foundWidgetInGroup);
						sortedWidgets.push(foundWidgetInGroup);
					}
				}
			}
		}
		// console.warn('this.localStorageAsOptions', this.localStorageAsOptions)
		this._widgets = [...sortedWidgets].reverse(); // reverse to keep order from Redux
	}

	/** static */
	private _setFontInConfig(val: string, conf: any) {
		if (val && conf) {
			const indexOfDelim = val.indexOf(';');
			if (indexOfDelim > -1) { // found
				conf.embeddedfont = val.substring(0, indexOfDelim); // font clip id
				conf.font = val.substring(indexOfDelim + 1); // font family
			}
		}
	}

	/** quasi-static */
	private _expandComplexKey(key: string, val: any, sourceConfig: any, targetConfig: any) {
		const keys: string[] = key.split('/');
		if (sourceConfig && typeof sourceConfig[keys[0]] === 'object') {
			targetConfig[keys[0]] = JSON.parse(JSON.stringify(sourceConfig[keys[0]])); // preserve existing
		}
		let tree: any = targetConfig; // ref
		for (let j = 0, jl = keys.length; j < jl - 1; j++) { // NB range -1!
			if (typeof tree[keys[j]] !== 'object') {
				tree[keys[j]] = {};
			}
			tree = tree[keys[j]]; // ref
		}
		tree[keys[keys.length - 1]] = val;

		// exception for font
		if (keys[keys.length - 1] === 'font') {
			this._setFontInConfig(val, tree);
		}
	}

	private getClipProjectRelation(clip) {
		const relations = this.mediaclipService.getRelations(clip);
		if (relations?.project?.length) {
			this.sapiService.get('project', relations.project[0]).subscribe((p) => {
				const project = p as Project;
				this.parentProject = project;
				this.projectContextOptions = project?.contexts?.map(c => ({
					label: c.label,
					value: c.code
				})) || [];
				this.projectContextOptionsChange.next(this.projectContextOptions);
			});
		}
	}

	private addSelectorOptions(widget: IWidget) {
		const w = widget as any;
		if (w && w.config) {
			this.widgetsAsOptions.push({
				label: w.label,
				value: w.id,
				type: w.type,
				prefixHtml: this.widgetIconPipe.transform(w.type, false)
			});
			// leaving some of below code as a comment, we need it when we want to support values in all statesets

			// if (w && w.config && w.config.stateSets && w.config.stateSets.length) {
			// w.config.stateSets.forEach((stateSet: any) => {
			// 	if (stateSet && stateSet.states && stateSet.states.length) {
			// 		stateSet.states.forEach((state: any) => {
			// 			if (state && state.config) {
			Object.keys(w.config).forEach((configKey) => {
				// Object.keys(state.config).forEach((configKey) => {
				if (configKey.indexOf('on') === 0) {
					// const ev = state.config[configKey];
					const ev = w.config[configKey];
					if (ev && ev.length) {
						ev.forEach((part: any) => {
							if (part && part.condition && part.condition.variables && part.condition.variables.length) {
								part.condition.variables.forEach((conditionVariable: any) => {
									if (conditionVariable && conditionVariable.entityType && conditionVariable.rule) {
										switch (conditionVariable.entityType) {
											case 'localstorage':
												if (
													conditionVariable.rule.lvalue && typeof conditionVariable.rule.lvalue !== 'object'
													&& this.localstorageKeys.indexOf(conditionVariable.rule.lvalue) === -1
												) {
													this.localstorageKeys.push(conditionVariable.rule.lvalue);
												}
												if (
													conditionVariable.rule.rvalue && typeof conditionVariable.rule.rvalue !== 'object'
													&& this.localstorageValues.indexOf(conditionVariable.rule.rvalue) === -1
												) {
													this.localstorageValues.push(conditionVariable.rule.rvalue);
												}
												break;

											case 'compare':
												if (conditionVariable.rule.lvalue && this.variableOptions.indexOf(conditionVariable.rule.lvalue) === -1) {
													this.variableOptions.push(conditionVariable.rule.lvalue);
												}
												break;
										}
									}
								});
							}
							if (part && part.actions && part.actions.length) {
								part.actions.forEach((action: IAction) => {
									if (action.entity === 'form' && action.command === 'submit') {
										this.haveFormSubmit = true;
									}
									if (action.entity === 'message') { // remove this if block when #message TODO below is implemented
										if (this.messageOptions.indexOf(action.command) === -1) {
											this.messageOptions.push(action.command);
										}
									}
									if (action && action.parameters && action.parameters.length) {
										action.parameters.forEach((actionParam) => {
											if (actionParam.name === 'p_0' && actionParam.value && typeof actionParam.value !== 'object') {
												switch (action.entity) {
													case 'localstorage':
														if (this.localstorageKeys.indexOf(actionParam.value) === -1) {
															this.localstorageKeys.push(actionParam.value);
														}
														break;
													case 'compare':
														if (this.variableOptions.indexOf(actionParam.value) === -1) {
															this.variableOptions.push(actionParam.value);
														}
														break;
													case 'message':
														// #message TODO: timeline converter (and player(!) should support this)
														if (this.messageOptions.indexOf(actionParam.value) === -1) {
															this.messageOptions.push(actionParam.value);
														}
														break;
												}
											}
											if (actionParam.name === 'p_1' && actionParam.value && typeof actionParam.value !== 'object') {
												switch (action.entity) {
													case 'localstorage':
														if (this.localstorageValues.indexOf(actionParam.value) === -1) {
															this.localstorageValues.push(actionParam.value);
														}
														break;
													case 'compare':
														if (this.variableOptions.indexOf(actionParam.value) === -1) {
															this.variableOptions.push(actionParam.value);
														}
														break;
												}
											}
										});
									}
								});
							}
						});
					}
				}
			});
			// 			}
			// 		})
			// 	}
			// });
		}
	}

	// manage order data into runtime order data
	private manageTracks(iDataOrder: IDataTrackAreaItem[], iDataWidgets: IDataWidget[]): void {

		// first, check if 1 or more tracks(trackGroups) have been removed and remove them from _tracks
		let i: number = this._tracks.length;
		while (i--) {
			const dataItem: IDataTrackAreaItem = iDataOrder.find(o => o && this._tracks[i] && o.id === this._tracks[i].id);
			if (dataItem === undefined) {
				this._tracks.splice(i, 1);
			}
		}

		//  next, check if tracks(trackgroups) are added or changed
		for (let j = 0, l: number = iDataOrder.length; j < l; j++) {
			if (!iDataOrder[j]) {
				continue;
			} // sanity
			const item: ITrackAreaItem = this._tracks.find(o => iDataOrder[j] && o && o.id === iDataOrder[j].id);
			if (item === undefined) {
				// we don't have this item yet, adding it
				let newItem: ITrackAreaItem;
				if (iDataOrder[j].type === 'track') {
					newItem = new Track(iDataOrder[j]);
				} else if (iDataOrder[j].type === 'trackgroup') {
					newItem = new TrackGroup(iDataOrder[j]);
				}
				if (newItem) {
					this._tracks.push(newItem);
				}
			} else {
				// item already exists, always update
				item.update(iDataOrder[j]); // sets fromAppId & fromAppTrackId from data
			}
		}

		// sort tracks by redux data
		const sortedOrder: ITrackAreaItem[] = iDataOrder.map(item => this._tracks.find(o => o && item && o.id === item.id));
		this._tracks = [...sortedOrder].reverse();
		this._tracks.forEach((track: ITrackAreaItem) => {
			if (track && track instanceof TrackGroup && track.tracks) {
				// reverse track order within groups
				track.tracks = [...track.tracks].reverse();
			}
		});
	}
	// private hasChanged( arg1: any, arg2: any): boolean {
	// 	try {
	// 		if ( JSON.stringify(arg1) === JSON.stringify(arg2) ) {
	// 			return false;
	// 		}
	// 	} catch (err) {
	// 		return true;
	// 	}
	// 	return true;
	// }

	private mapTracks(trackItem: ITrackAreaItem): any {
		return trackItem instanceof TrackGroup && trackItem.tracks ? {
			id: trackItem.id,
			type: 'TrackGroup',
			tracks: [...trackItem.tracks].map(trackInGroupItem => this.mapTracks(trackInGroupItem)).reverse()
		} : {
			id: trackItem.id,
			type: 'Track',
			widgetId: trackItem.widgetId,
		};
	}

	private _insertDataTrackCopy(track: IDataTrackAreaItem, appWidgetId: string, appWidgetTrackId: string,
		newTracks: IDataTrackAreaItem[], existingTracks: IDataTrackAreaItem[]) {

		const trackCopy: IDataTrackAreaItem = JSON.parse(JSON.stringify(track));
		if (appWidgetId) {
			trackCopy.widgetid = appWidgetId + '.' + trackCopy.widgetid; // NB!
		}
		if (appWidgetTrackId) {
			trackCopy.id = appWidgetTrackId + '.' + trackCopy.id; // NB!
			trackCopy.fromAppTrackId = appWidgetTrackId;
		}
		newTracks.push(trackCopy);

		const trackCopyTrackIndex = existingTracks.findIndex(element => element && element.id === trackCopy.id);
		if (trackCopyTrackIndex >= 0) { // found; remove, will re-insert at correct index
			existingTracks.splice(trackCopyTrackIndex, 1);
		}
	}

	private _insertDataWidgetCopy(widget: IDataWidget, appWidgetId: string, appWidget: IDataWidget, newWidgets: IDataWidget[], existingWidgets: IDataWidget[]) {
		const widgetCopy: IDataWidget = JSON.parse(JSON.stringify(widget));
		if (appWidgetId) {
			widgetCopy.id = appWidgetId + '.' + widgetCopy.id; // NB!
		}
		if (appWidget) {
			InteractivityEditorService.overrideStatesets(appWidget, widgetCopy);
		}
		newWidgets.push(widgetCopy);

		const widgetCopyIndex = existingWidgets.findIndex(element => element && element.id === widgetCopy.id);
		if (widgetCopyIndex >= 0) { // found; remove, will re-insert at correct index
			existingWidgets.splice(widgetCopyIndex, 1);
		}
	}
}
